import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../network/ApiCall';
import LoadingSpinner from '../widgets/LoadingSpinner';

const SystemSettingsTable = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    const loadSettings = async () => {
      setLoading(true);
      setTableLoading(true);

      try {
        const response = await axiosInstance.get('/settings');
        if (ignore) {
          return;
        }

        const data = response?.data;
        if (Array.isArray(data)) {
          // sort by country code
          data.sort((a, b) => a.countryCode.localeCompare(b.countryCode));
          setSettings(data);
        } else if (data) {
          setSettings([data]);
        } else {
          setSettings([]);
        }
      } catch (error) {
        console.error('ERROR LOADING SYSTEM SETTINGS TABLE', error);
        if (!ignore) {
          setSettings([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
          setTableLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      ignore = true;
    };
  }, []);

  const tableData = useMemo(() => (
    settings.map((row) => {
      const countryCode = row?.countryCode?.toUpperCase?.() ?? '';
      return {
        ...row,
        countryCode,
      };
    })
  ), [settings]);

  const columns = useMemo(() => ([
    {
      name: t('systemSettings:lblCountry'),
      selector: (row) => row.countryCode ?? '',
      sortable: true,
      cell: (row) => {
        const countryCode = row.countryCode ?? '';
        const countryName = countryCode
          ? t(`countries:${countryCode}`, { defaultValue: countryCode })
          : t('common:lblNotSpecified');
        const label = countryCode ? `${countryCode} - ${countryName}` : countryName;

        if (!countryCode) {
          return <span>{label}</span>;
        }

        return (
          <Link to={`/app/settings/${countryCode}`} className="btn btn-link p-0">
            {label}
          </Link>
        );
      },
    },
    {
      name: t('systemSettings:lblSenderId'),
      selector: (row) => row.senderId ?? '',
      sortable: true,
      cell: (row) => row.senderId || t('common:lblNotSpecified'),
    }
  ]), [t]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <DataTable
      noHeader
      columns={columns}
      data={tableData}
      striped
      progressPending={tableLoading}
    />
  );
};

export default SystemSettingsTable;